import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Icon, DropdownMenu, Stack } from '@hotjar/hotsauce';
import { AdvancedSelect } from '@hotjar/webapp/components/AdvancedSelect';

import type { TargetingOption, TargetingOptionValue } from '@hotjar/heatmaps';
import { TARGETING_VALUE, TARGETING_OPTION_VALUE, TARGETING_VALUE_LABELS } from '@hotjar/heatmaps';
import { useTranslation } from 'react-i18next';

export const ALL_PAGES_OPTIONS: TargetingOption[] = [
  {
    icon: 'page_empty',
    label: TARGETING_VALUE_LABELS[TARGETING_VALUE.SIMPLE],
    value: TARGETING_OPTION_VALUE.SINGLE_PAGE,
  },
  {
    icon: 'page_empty',
    label: TARGETING_VALUE_LABELS[TARGETING_VALUE.EXACT],
    value: TARGETING_OPTION_VALUE.EXACT,
  },
  {
    icon: 'layers',
    label: TARGETING_VALUE_LABELS[TARGETING_VALUE.CONTAINS],
    value: TARGETING_OPTION_VALUE.CONTAINS,
  },
  {
    icon: 'layers',
    label: TARGETING_VALUE_LABELS[TARGETING_VALUE.STARTS_WITH],
    value: TARGETING_OPTION_VALUE.STARTS_WITH,
  },
  {
    icon: 'layers',
    label: TARGETING_VALUE_LABELS[TARGETING_VALUE.ENDS_WITH],
    value: TARGETING_OPTION_VALUE.ENDS_WITH,
  },
  {
    icon: 'layers',
    label: TARGETING_VALUE_LABELS[TARGETING_VALUE.REGEX],
    value: TARGETING_OPTION_VALUE.REGEX,
  },
];

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const SelectLabel = styled.div``;

const SelectContainer = styled(Stack)`
  align-items: center;
`;

const StyledAdvancedSelect = styled(AdvancedSelect)`
  width: 220px;
  margin-right: 8px;
`;
interface TargetOptionsSelectorProps {
  className?: string;
  id?: string;
  selectedTargetingValue: TargetingOptionValue;
  onTargetChange: (value: TargetingOptionValue) => void;
}

export const findOptionByValue = (
  options: TargetingOption[],
  value: TargetingOptionValue,
): TargetingOption | undefined => options.find((option) => option.value === value);

export const TargetOptionsSelector = ({
  className,
  id,
  selectedTargetingValue,
  onTargetChange,
}: TargetOptionsSelectorProps): JSX.Element => {
  const { t } = useTranslation();
  const renderedOptions = ALL_PAGES_OPTIONS;

  const [selectedOption, setSelectedOption] = useState(
    findOptionByValue(renderedOptions, selectedTargetingValue) || ALL_PAGES_OPTIONS[0],
  );

  const handleOptionSelect = (option: TargetingOption) => {
    setSelectedOption(option);
    onTargetChange(option.value);
  };

  const renderOptions = () =>
    renderedOptions.map((option) => (
      <DropdownMenu.Item
        key={option.value}
        icon={option.icon}
        isSelected={option.value === selectedOption.value}
        onClick={() => handleOptionSelect(option)}
      >
        {t(option.label)}
      </DropdownMenu.Item>
    ));

  useEffect(() => {
    setSelectedOption(
      findOptionByValue(renderedOptions, selectedTargetingValue) || ALL_PAGES_OPTIONS[0],
    );
  }, [renderedOptions, selectedTargetingValue]);

  return (
    <StyledAdvancedSelect
      size="m"
      ariaLabel={selectedOption.label}
      appendTo="parent"
      className={className}
      id={id}
      selectContent={
        <SelectContainer id="targetingSelector" spacing="x1">
          <StyledIcon icon={selectedOption.icon} />
          <SelectLabel>{t(selectedOption.label)}</SelectLabel>
        </SelectContainer>
      }
    >
      {renderOptions()}
    </StyledAdvancedSelect>
  );
};
